/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import req from '@sportninja/common/api/request'
import { ENABLE_REGISTRATION, ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { useHistory, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import Page from '../../components/Page'
import FormSheet from '../../components/Form/FormSheet'
import { feedItemForm, teamForm } from '../../components/Form/form-configs'
import FavouriteButton from '../../components/Page/FavouriteButton'
import PlayerList from './PlayerListTable'
import { AddTeamOfficial } from '../../components/List/TeamOfficialList'
import GameList from '../../components/List/GameList'
import NewButton from '../../components/Page/NewButton'
import FileDownload from '../../components/FileDownload'
import BaseActionButton from '../../components/Page/BaseActionButton'
import { Mobile } from '../../components/Responsive'
import EditButton from '../../components/Page/EditButton'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import ImportTeamRoster from '../../components/ImportCSV/ImportTeamRoster'
import { getBreadcrumbs, useReadItem, useStoreRecent } from '../utils'
import EditEntitySubTitle from '../EditEntitySubTitle'
import CreatePlayerSheet from './CreatePlayerSheet'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  getAllGenericSportProperties,
  getHockeySport,
  getSoccerSport,
} from '@sportninja/common/selectors/types'
import { NewModal } from 'src/components/NewModal'
import { Button } from 'src/components/Button'
import calendarImg from './calendar.svg'
import outlookIcon from './outlook-icon.svg'
import calendarIcon from './calendar-icon.svg'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import { Input } from 'src/components/Input'
import TeamStatistics from 'src/components/v2/TeamStatistics'
import { useSports } from '@sportninja/common/hooks/useSports'
import GeneratePluginModal from 'src/components/GeneralPluginModal'
import {
  shouldAllowAddPlayer,
  shouldAllowAddImportRoster,
  shouldAllowAddTeamOfficials,
} from '@sportninja/common/ts-utils/add-roster-utils'
import TeamOfficialListTable from 'src/components/List/TeamOfficialListTable'
import FinancialListTable from 'src/components/List/FinancialListTable'
import EntityFeed from 'src/components/EntityFeed'

const Team = ({
  match,
  isInFrame,
  soccerSportId,
  hockeySportId,
  genericSportProperties,
}) => {
  const history = useHistory()
  const location = useLocation()
  const [rosterId, setRosterId] = useState('')
  const [players, setPlayers] = useState([])
  const [creatingPlayersRequiresEmail, setCreatingPlayersRequiresEmail] =
    useState(false)
  const [shouldShowWaiver, setShouldShowWaiver] = useState(false)
  const [areFiltersHidden, setAreFiltersHidden] = useState(false)
  const [filterForExporting, setFilterForExporting] = useState(null)
  const [calendarBlob, setCalendarBlob] = useState(null)
  const [calendarURL, setCalendarURL] = useState(null)
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const { sports, sportOptions, fetchSports } = useSports()

  useEffect(() => {
    if (!sports || sports?.length === 0) {
      fetchSports()
    }
  }, [fetchSports, sports])

  const [lockRosterDate, setLockRosterDate] = useState(null)
  const [canAddPlayersFromRoster, setCanAddPlayersFromRoster] = useState(false)
  const [isRegistrationOn, setIsRegistrationOn] = useState(false)
  const isCurrentDateAfterDivisionOrCompetitionLockDate = useMemo(() => {
    if (!lockRosterDate) {
      return false
    }
    if (lockRosterDate) {
      return new Date(lockRosterDate) < new Date()
    }
  }, [lockRosterDate])
  // get the sn_schedule from the url
  const urlParams = new URLSearchParams(window.location.search)
  const scheduleId = urlParams.get('sn_schedule')

  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    readItem,
    refreshKey,
    triggerRefresh,
  } = useReadItem()

  const sport = useMemo(() => {
    return sports?.find((s) => s.id === item?.sport?.id)
  }, [sports, item?.sport?.id])

  const genericSport = useMemo(() => {
    if (item?.generic_sport_id) {
      return genericSportProperties.find((s) => s.id === item.generic_sport_id)
    }
    return null
  }, [item, genericSportProperties])
  const isGenericSport = genericSport !== null && genericSport?.id !== null

  const sync_salesforce = item?.organization?.sync_salesforce || false

  const itemSportFullName = useMemo(() => {
    return sports?.find((sport) => sport.id === item?.sport?.id)?.name_full
  }, [sports, item?.sport?.id])

  const loadCalendarICS = useCallback(async () => {
    const res = await req(`/calendar/${TEAM_ID}/games`, {
      parseJSON: false,
    })

    if (res?.url) {
      setCalendarURL(res.url)
      setCalendarBlob(await res.blob())
    }
  }, [])

  const TEAM_ID = match.params.id?.replace('?sandbox_tour=1', '')
  const BASE_ROUTE = `${ROUTES.TEAM_ROOT}/${TEAM_ID}`
  const IS_PUBLIC = item.is_public
  const isSoccer = item?.sport?.id === soccerSportId
  const isHockey = item?.sport?.id === hockeySportId

  const calendarSyncModalRef = useRef()
  const generatePluginTagModalRef = useRef(null)

  useStoreRecent(ENTITY_TYPES.team, item, error, TEAM_ID, isInFrame)

  useEffect(() => {
    readItem(ENTITY_TYPES.team, TEAM_ID)
  }, [TEAM_ID])

  const onEditTeam = async (values) => {
    const { imageData, backgroundImageData, ...form } = values

    if (form?.name_full) {
      form.name = form.name_full
    }

    if (form?.delete_image) {
      await req(`/teams/${item.id}/image`, {
        method: 'DELETE',
      })
    }

    if (form?.delete_image_background) {
      await req(`/teams/${item.id}/background-image`, {
        method: 'DELETE',
      })
    }

    const body = JSON.stringify(form)
    const response = await req(`/teams/${item.id}`, {
      method: 'PUT',
      body,
    })
    if (imageData) {
      await req(`/teams/${item.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }
    if (backgroundImageData) {
      await req(`/teams/${item.id}/background-image`, {
        method: 'POST',
        body: backgroundImageData,
      })
    }
    await readItem(ENTITY_TYPES.team, TEAM_ID)
    return response
  }

  const shouldEnableAddPlayer = useMemo(() => {
    return shouldAllowAddPlayer({
      adminPermission: permission.admin,
      updatePermission: permission.update,
      rosterId,
      sync_salesforce,
      organization: item.organization,
      isCurrentDateAfterDivisionOrCompetitionLockDate,
      canAddPlayersFromRoster,
      isRegistrationOn,
    })
  }, [
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    isRegistrationOn,
    item.organization,
    permission,
    rosterId,
    sync_salesforce,
  ])

  // Derive a reason to show in a tooltip when Add Player is disabled
  const addPlayerDisabledReason = useMemo(() => {
    if (shouldEnableAddPlayer) {
      return null
    }
    // Permission checks first
    if (!permission?.admin && !permission?.update) {
      return 'You Do Not Have Permission To Manage The Roster.'
    }
    if (!rosterId) {
      return 'Roster Is Not Available For This Team.'
    }
    // Registration-specific restriction
    if (isRegistrationOn && !canAddPlayersFromRoster) {
      return 'Registration Is Turned On, Add Players Via Registration.'
    }
    // Lock date restriction
    if (isCurrentDateAfterDivisionOrCompetitionLockDate) {
      return 'Roster Is Locked For This Competition.'
    }
    // Salesforce restriction (for Canlan brands)
    if (isCanlan && sync_salesforce && !permission?.admin) {
      return 'Roster Is Managed By Salesforce.'
    }
    return 'Action Unavailable.'
  }, [
    shouldEnableAddPlayer,
    permission?.admin,
    permission?.update,
    rosterId,
    isRegistrationOn,
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    sync_salesforce,
  ])

  const isMember = useMemo(
    () =>
      permission?.member ||
      permission?.admin ||
      permission?.update ||
      permission?.post,
    [
      permission?.member,
      permission?.admin,
      permission?.update,
      permission?.post,
    ]
  )

  const shouldEnableAddImportRoster = useMemo(() => {
    return shouldAllowAddImportRoster({
      adminPermission: permission?.admin,
      updatePermission: permission?.update,
      rosterId,
      sync_salesforce,
      isCurrentDateAfterDivisionOrCompetitionLockDate:
        isCurrentDateAfterDivisionOrCompetitionLockDate,
      isRegistrationOn,
      canAddPlayersFromRoster,
    })
  }, [
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    isRegistrationOn,
    permission?.admin,
    permission?.update,
    rosterId,
    sync_salesforce,
  ])

  // Derive a reason to show in a tooltip when Import Roster is disabled
  const importRosterDisabledReason = useMemo(() => {
    if (shouldEnableAddImportRoster) {
      return null
    }
    if (!permission?.admin && !permission?.update) {
      return 'You Do Not Have Permission To Import Rosters.'
    }
    if (!rosterId) {
      return 'Roster Is Not Available For This Team.'
    }
    // Registration-specific restriction
    if (isRegistrationOn && !canAddPlayersFromRoster) {
      return 'Registration Is Turned On, Add Players Via Registration.'
    }
    if (!isCanlan && isCurrentDateAfterDivisionOrCompetitionLockDate) {
      return 'Roster Is Locked For This Competition.'
    }
    if (isCanlan && sync_salesforce && !permission?.admin) {
      return 'Roster Is Managed By Salesforce.'
    }
    return 'Action Unavailable.'
  }, [
    shouldEnableAddImportRoster,
    permission?.admin,
    permission?.update,
    rosterId,
    isRegistrationOn,
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    sync_salesforce,
  ])

  const shouldEnableAddTeamOfficials = useMemo(() => {
    return shouldAllowAddTeamOfficials({
      rosterId,
      sync_salesforce,
      organization: item?.organization,
      adminPermission: permission?.admin,
      updatePermission: permission?.update,
    })
  }, [permission, rosterId, sync_salesforce, item?.organization])

  const titleActions = item?.id
    ? [
        <FavouriteButton
          key='favourite'
          initialState={item.is_favorite}
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
        />,
        permission.update && (
          <FormSheet
            customDeleteModal={
              permission.delete && (
                <EntityFormDelete
                  entityType={ENTITY_TYPES.team}
                  itemId={TEAM_ID}
                  title={`Delete ${item.name_full}`}
                  type={t('common:team')}
                  onComplete={() => {
                    history.push(`${ROUTES.TEAMS.ROOT}`)
                  }}
                />
              )
            }
            key='edit'
            Button={({ onClick }) => (
              <EditButton onClick={onClick} title={item?.name_full} />
            )}
            form={teamForm(
              parsers[ENTITY_TYPES.team]({ ...item, permission }),
              false,
              itemSportFullName,
              sportOptions,
              sports,
              parsers[ENTITY_TYPES.team]({ ...item, permission })?.sport_id,
              shouldEnableAddPlayer
            )}
            label={t('Web:editTeam')}
            onSubmit={onEditTeam}
            title={t('Web:editTeam')}
            subTitle={
              <EditEntitySubTitle id={TEAM_ID} entityType={ENTITY_TYPES.team} />
            }
          />
        ),
      ]
    : []

  const baseActions = []

  const filterToggleButton = (
    <BaseActionButton
      faType={areFiltersHidden ? 'far' : 'fas'}
      key='toggle-filter'
      name='filter'
      title={`Toggle Filters Visibility ${areFiltersHidden ? 'on' : 'off'}`}
      onClick={() => {
        setAreFiltersHidden((v) => !v)
      }}
    />
  )

  const calendarSyncButton = (
    <BaseActionButton
      key='toggle-calendar-sync'
      customIcon={
        <img
          src={calendarIcon}
          css={css`
            width: 16px;
            height: 16px;
          `}
        />
      }
      title='Schedule Calendar Sync'
      onClick={async () => {
        loadCalendarICS()
        calendarSyncModalRef.current?.openModal()
      }}
    />
  )

  const generatePluginModalButton = permission?.update ? (
    <BaseActionButton
      key='plug'
      name='plug'
      title={'Generate JS Plugin Tag'}
      onClick={() => {
        generatePluginTagModalRef?.current?.openModal()
      }}
    />
  ) : null

  const pages = [
    {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/schedule`,
      text: t('common:schedule'),
      component: (
        <GameList
          team_id={TEAM_ID}
          areFiltersHidden={areFiltersHidden}
          setFilterForExporting={setFilterForExporting}
          sportId={item?.sport?.id}
          isSoccer={isSoccer}
          isHockey={isHockey}
          scheduleId={scheduleId}
        />
      ),
      actions: [
        generatePluginModalButton,
        calendarSyncButton,
        filterToggleButton,
      ],
    },
    !isGenericSport && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/statistics`,
      text: t('common:statistics'),
      component: (
        <TeamStatistics
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
          areFiltersHidden={areFiltersHidden}
          autoSelectMostRecentComp
          isSoccer={item?.sport?.id === soccerSportId}
          displaySearch={false}
          sportId={item?.sport?.id}
        />
      ),
      actions: [filterToggleButton],
    },
    !item?.is_placeholder && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/roster`,
      text: t('common:roster'),
      component: (
        <PlayerList
          refreshKey={refreshKey}
          teamId={TEAM_ID}
          teamName={item?.name_full}
          permission={permission}
          onComplete={(data) => {
            setLockRosterDate(data.lock_roster_date)
            setCanAddPlayersFromRoster(data?.can_add_players_from_roster)
            setIsRegistrationOn(data?.is_registration_on)
            setRosterId(data.id)
            setPlayers(data.players)
            setCreatingPlayersRequiresEmail(data.require_email)
            setShouldShowWaiver(data.should_show_waiver)
          }}
          shouldShowWaiverStatus={shouldShowWaiver}
          isSoccer={item?.sport?.id === soccerSportId}
          sportName={item?.sport?.name}
          sportId={item?.sport?.id}
          lockRosterDate={lockRosterDate}
        />
      ),
      actions: [
        permission.update && rosterId && (
          <FileDownload
            key='download-roster'
            button={(loading, onClick) => {
              return (
                <BaseActionButton
                  loading={loading}
                  onClick={onClick}
                  name='download'
                  title='Export Roster'
                />
              )
            }}
            label={t('common:exportRoster')}
            url={`/teams/${TEAM_ID}/rosters/${rosterId}/players/export/csv`}
            fileName={item.name_full}
            query={filterForExporting}
          />
        ),
        (permission.update || permission.admin) && rosterId && (
          <ImportTeamRoster
            key='import-roster'
            teamId={TEAM_ID}
            teamName={item.name_full}
            teamRosterId={rosterId}
            onSuccess={triggerRefresh}
            disabled={!shouldEnableAddImportRoster}
            disabledReason={importRosterDisabledReason}
          />
        ),
        (permission.update || permission.admin) && rosterId && (
          <CreatePlayerSheet
            key='add-player'
            teamId={TEAM_ID}
            orgId={item?.organization?.id}
            orgName={item?.organization?.name_full}
            rosterId={rosterId}
            players={players}
            hasAdminPermission={permission?.admin}
            creatingPlayersRequiresEmail={creatingPlayersRequiresEmail}
            onSuccess={triggerRefresh}
            sportName={item?.sport?.name}
            sportId={item?.sport?.id}
            disabled={!shouldEnableAddPlayer}
            disabledReason={addPlayerDisabledReason}
          />
        ),
      ],
    },
    isMember && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/feed`,
      text: t('common:feed'),
      component: (
        <EntityFeed
          entity={item}
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
          refreshKey={refreshKey}
          canPost={permission.post || permission.update || permission.admin}
          canTargetAudience={permission.update || permission.admin}
        />
      ),
      actions: [
        permission.admin && (
          <Mobile key='add-post'>
            <FormSheet
              label={t('Web:createANewPost')}
              title={t('Web:createANewPost')}
              form={feedItemForm({}, false)}
              onSubmit={async ({ imageData, ...form }) => {
                const body = JSON.stringify(form)
                const response = await req(`/teams/${TEAM_ID}/social-feed`, {
                  method: 'POST',
                  body,
                })
                if (imageData) {
                  await req(`/social-feed/${response.data.id}/image`, {
                    method: 'POST',
                    body: imageData,
                  })
                }
                triggerRefresh()
              }}
              Button={({ onClick }) => <NewButton onClick={onClick} />}
            />
          </Mobile>
        ),
      ],
    },
    !isInFrame &&
      ENABLE_REGISTRATION &&
      !isCanlan &&
      permission.update &&
      !item?.is_placeholder && {
        to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/billing`,
        text: t('common:billing'),
        privatePage: true,
        component: (
          <FinancialListTable canEdit={permission.update} teamId={TEAM_ID} />
        ),
        actions: [],
      },
    !isInFrame &&
      permission.update &&
      !item?.is_placeholder && {
        to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/team-officials`,
        text: t('common:teamOfficial_plural', { count: 2 }),
        privatePage: true,
        component: (
          <TeamOfficialListTable
            key='team-officials'
            teamId={TEAM_ID}
            teamName={item?.name_full}
            permission={permission}
            onComplete={(data) => {
              setRosterId(data.id)
            }}
            refreshKey={refreshKey}
            sportName={item?.sport?.name}
            sportId={item?.sport?.id}
          />
        ),
        actions: [
          shouldEnableAddTeamOfficials && (
            <AddTeamOfficial
              key='add-team-official'
              teamId={TEAM_ID}
              rosterId={rosterId}
              permission={permission}
              onComplete={triggerRefresh}
              sportName={item?.sport?.name}
              sportId={item?.sport?.id}
            />
          ),
        ],
      },
  ]

  return (
    <>
      <Page
        contactInfo={{
          email: item?.email,
          website: item?.website,
          phone: item?.phone,
          support_email: item?.support_email,
          support_website: item?.support_website,
          support_phone: item?.support_phone,
        }}
        sport={sport}
        genericSport={genericSport}
        isSoccer={isSoccer}
        backgroundImageUrl={item?.background_image?.full_path}
        isTeamOrSchedule
        isTeam={true}
        error={error}
        loading={loading}
        baseCrumb={
          error
            ? ROUTES.TEAMS.ROOT
            : `${ROUTES.ORG_ROOT}/${item?.organization?.id}/teams`
        }
        baseRoute={BASE_ROUTE}
        baseTitle={item?.organization?.name_full}
        breadcrumbs={getBreadcrumbs(ROUTES.TEAM_ROOT, item, hierarchy)}
        baseActions={baseActions}
        pages={pages}
        isPrivate={!IS_PUBLIC}
        title={item.name_full}
        subTitle={item?.generic_sport_name ? item?.generic_sport_name : null}
        organizationId={item?.organization?.id}
        iconName='user-friends'
        imageId={getImageThumbnailId(item)}
        titleActions={titleActions}
        preserveQueryFor={[
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/schedule`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/roster`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/statistics`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/billing`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/team-officials`,
        ]}
      />
      <NewModal shouldCloseOnOverlayClick ref={calendarSyncModalRef}>
        <div
          css={css`
            width: 700px;
            padding: 16px;
            background: no-repeat fixed linear-gradient(#282e38, #181a1d);
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            max-height: 80vh;

            @media (max-width: 500px) {
              width: 480px;
            }
          `}
        >
          <button
            onClick={(e) => {
              e.preventDefault()

              calendarSyncModalRef.current?.closeModal()
            }}
            css={css`
              position: absolute;
              top: 30px;
              right: 35px;
            `}
          >
            <Icon name='times' color={colors.ATTENDANCE_GRAY} fontSize={16} />
          </button>
          <div
            css={css`
              display: flex;
              width: 190px;
              height: 130px;
              align-self: center;
            `}
          >
            <img
              src={calendarImg}
              css={css`
                width: 100%;
                height: 100%;
              `}
            />
          </div>
          <div
            css={css`
              align-self: center;
            `}
          >
            <p
              css={css`
                color: ${colors.DEFAULT_FLAIR};
                text-align: center;
                margin: 24px 0;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
              `}
            >
              Subscribe to Calendar
            </p>
            <p
              css={css`
                color: ${colors.WHITE};
                text-align: center;
                font-size: 14px;
                font-style: normal;
                margin-bottom: 16px;
                font-weight: 500;
              `}
            >
              Bring your team calendar to your phone calendar and get all your
              games/events/practices to show up automatically in one place.
            </p>
            <p
              css={css`
                color: ${colors.ATTENDANCE_GRAY};
                text-align: center;
                font-size: 12px;
                font-style: normal;
                font-weight: 500;
              `}
            >
              To subscribe, select one of the following options, or copy the
              address and subscribe with your favourite email client.
            </p>
          </div>
          <div
            css={css`
              margin-top: 24px;
              display: flex;
              align-items: center;
              align-self: center;
              gap: 24px;

              button {
                gap: 0;
                width: 200px;
                justify-content: center;
              }
            `}
          >
            <Button
              label='GOOGLE'
              labelStyle={css`
                font-family: Rift;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
              labelColor={colors.HEADER}
              onClick={() => {
                const link = document.createElement('a')
                link.href = `https://calendar.google.com/calendar/r?cid=webcal%3A%2F%2F${calendarURL.replace(
                  'https://',
                  ''
                )}`
                link.target = '_blank'
                link.click()
              }}
              icon={
                <Icon
                  name='google'
                  fontSize={18}
                  color={colors.HEADER}
                  faType='fab'
                />
              }
            />
            <Button
              label='APPLE'
              labelStyle={css`
                font-family: Rift;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
              labelColor={colors.HEADER}
              onClick={() => {
                const link = document.createElement('a')
                link.href = `webcal://${calendarURL.replace('https://', '')}`
                link.target = '_self'
                link.click()
              }}
              icon={
                <Icon
                  name='apple'
                  fontSize={18}
                  color={colors.HEADER}
                  faType='fab'
                />
              }
            />
            <Button
              label='OUTLOOK'
              labelStyle={css`
                font-family: Rift;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
              labelColor={colors.HEADER}
              onClick={() => {
                const link = document.createElement('a')
                link.href = `webcal://${calendarURL.replace('https://', '')}`
                link.target = '_self'
                link.click()
              }}
              icon={
                <img
                  src={outlookIcon}
                  css={css`
                    width: 20px;
                    height: 20px;
                  `}
                />
              }
            />
          </div>
          <div
            css={css`
              background-color: ${colors.SOFT_STEEL};
              height: 1px;
              margin: 24px 0;
            `}
          />
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <div
              css={css`
                flex: 6;
              `}
            >
              <Input isMockInput mockValue={String(calendarURL)} />
            </div>
            <div
              css={css`
                flex: 1;
              `}
            >
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(calendarURL)
                  setHasCopiedLink(true)
                  setTimeout(() => {
                    setHasCopiedLink(false)
                  }, 2000)
                }}
                label={hasCopiedLink ? 'Copied!' : 'Copy'}
                labelColor={colors.HEADER}
              />
            </div>
          </div>
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <Button
              antVariant='link'
              label={`or download the ${item?.name_full
                ?.replace(' ', '-')
                ?.toLowerCase()}-calendar.ics file`}
              labelColor={colors.DEFAULT_FLAIR}
              labelStyle={css`
                font-family: Maison Neue;
                font-size: 14px;
                text-transform: none;
              `}
              onClick={() => {
                const link = document.createElement('a')
                link.href = URL.createObjectURL(calendarBlob)
                link.download = `${item?.name_full
                  ?.replace(' ', '-')
                  ?.toLowerCase()}-calendar.ics`
                link.click()
              }}
            />
          </div>
        </div>
      </NewModal>
      <NewModal
        ref={generatePluginTagModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <GeneratePluginModal
          onPressClose={() => {
            generatePluginTagModalRef?.current?.closeModal()
          }}
          scheduleId={item?.id}
          isOrganization={false}
          isTeam={true}
        />
      </NewModal>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
    soccerSportId: getSoccerSport(state)?.id,
    hockeySportId: getHockeySport(state)?.id,
    genericSportProperties: getAllGenericSportProperties(state),
  }
}

export default connect(mapStateToProps)(Team)
